if (!customElements.get('logo-list-slider')) {
  class LogoListSlider extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-logo-viewport]');
      this.track = this.querySelector('[data-logo-track]');
      this.nav = this.querySelector('[data-logo-nav]');
      this.prev = this.querySelector('[data-logo-prev]');
      this.next = this.querySelector('[data-logo-next]');
      this.dots = this.querySelector('[data-logo-dots]');
      this.items = Array.from(this.track ? this.track.children : []);
      this.page = 0;
      this._resizeTimer = null;
      this._layoutRAF = null;
      this._lastMode = this.mode();

      if (!this.viewport || !this.track || !this.items.length) return;

      if (this.prev) {
        this.prev.addEventListener('click', () => this.goTo(this.page - 1));
      }

      if (this.next) {
        this.next.addEventListener('click', () => this.goTo(this.page + 1));
      }

      this._onScroll = () => {
        if (this.mode() !== 'mobile') return;
        window.cancelAnimationFrame(this._scrollRAF);
        this._scrollRAF = window.requestAnimationFrame(() => this.syncMobilePage());
      };
      this.viewport.addEventListener('scroll', this._onScroll, { passive: true });

      this._onResize = () => {
        window.clearTimeout(this._resizeTimer);
        this._resizeTimer = window.setTimeout(() => {
          const nextMode = this.mode();
          const crossedBreakpoint = nextMode !== this._lastMode;
          this._lastMode = nextMode;
          this.refresh({ crossedBreakpoint });
        }, 140);
      };
      window.addEventListener('resize', this._onResize, { passive: true });
      this._onImageLoad = () => this.refresh({ crossedBreakpoint: false });
      this.addEventListener('load', this._onImageLoad, true);

      this._onSectionChange = () => {
        this.syncItems();
        this.refresh({ crossedBreakpoint: true });
      };
      document.addEventListener('shopify:block:select', this._onSectionChange);
      document.addEventListener('shopify:block:deselect', this._onSectionChange);
      document.addEventListener('shopify:section:load', this._onSectionChange);

      this.refresh({ crossedBreakpoint: true });
    }

    disconnectedCallback() {
      if (this._onResize) window.removeEventListener('resize', this._onResize);
      if (this._onScroll && this.viewport) this.viewport.removeEventListener('scroll', this._onScroll);
      if (this._resizeTimer) window.clearTimeout(this._resizeTimer);
      if (this._scrollRAF) window.cancelAnimationFrame(this._scrollRAF);
      if (this._layoutRAF) window.cancelAnimationFrame(this._layoutRAF);
      if (this._onImageLoad) this.removeEventListener('load', this._onImageLoad, true);
      if (this._onSectionChange) {
        document.removeEventListener('shopify:block:select', this._onSectionChange);
        document.removeEventListener('shopify:block:deselect', this._onSectionChange);
        document.removeEventListener('shopify:section:load', this._onSectionChange);
      }
    }

    mode() {
      const width = window.innerWidth;
      if (width <= 767) return 'mobile';
      if (width <= 1067) return 'tablet';
      return 'desktop';
    }

    columnsPerRow() {
      const style = getComputedStyle(this.closest('.logo-list'));
      const currentMode = this.mode();
      const key = currentMode === 'mobile'
        ? '--logo-list-columns-mobile'
        : currentMode === 'tablet'
          ? '--logo-list-columns-tablet'
          : '--logo-list-columns-desktop';

      return Math.max(1, parseInt(style.getPropertyValue(key), 10) || 1);
    }

    pageSize() {
      if (this.mode() === 'mobile') return 7;
      return this.columnsPerRow() * 2;
    }

    syncItems() {
      this.items = Array.from(this.track ? this.track.children : []);
    }

    pageCount() {
      this.syncItems();
      return Math.max(1, Math.ceil(this.items.length / this.pageSize()));
    }

    refresh({ crossedBreakpoint = false } = {}) {
      if (crossedBreakpoint) {
        this.page = 0;
        this.track.style.transform = '';
        this.viewport.scrollLeft = 0;
      }

      this.pages = this.pageCount();
      this.page = Math.min(this.page, this.pages - 1);
      this.renderDots();

      /*
       * 等待媒体查询和 Grid 列宽真正完成切换后再计算。
       * 同时重新触发 lazySizes 的 autoSizer，避免图片在手机端
       * 记录了较小的 sizes 值，回到电脑端后仍保持小尺寸。
       */
      window.cancelAnimationFrame(this._layoutRAF);
      this._layoutRAF = window.requestAnimationFrame(() => {
        this._layoutRAF = window.requestAnimationFrame(() => {
          this.update();

          if (this.nav) {
            this.nav.hidden = this.pages <= 1;
          }
        });
      });
    }

    renderDots() {
      if (!this.dots) return;

      this.dots.innerHTML = '';
      for (let i = 0; i < this.pages; i += 1) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'logo-list-slider__dot';
        dot.setAttribute('aria-label', `第 ${i + 1} 页`);
        dot.addEventListener('click', () => this.goTo(i));
        this.dots.appendChild(dot);
      }
    }

    goTo(index) {
      const nextPage = Math.max(0, Math.min(index, this.pages - 1));
      this.page = nextPage;

      if (this.mode() === 'mobile') {
        const distance = this.mobilePageDistance();
        this.viewport.scrollTo({ left: distance * this.page, behavior: 'smooth' });
        this.updateControls();
        return;
      }

      this.update();
    }

    mobilePageDistance() {
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const first = this.items[0];
      if (!first) return this.viewport.clientWidth;
      return (first.getBoundingClientRect().width + gap) * 3;
    }

    syncMobilePage() {
      const distance = this.mobilePageDistance();
      if (!distance) return;
      this.page = Math.max(
        0,
        Math.min(this.pages - 1, Math.round(this.viewport.scrollLeft / distance))
      );
      this.updateControls();
    }

    updateControls() {
      if (this.prev) this.prev.disabled = this.page === 0;
      if (this.next) this.next.disabled = this.page >= this.pages - 1;

      if (!this.dots) return;

      Array.from(this.dots.children).forEach((dot, index) => {
        dot.classList.toggle('is-active', index === this.page);
        dot.setAttribute('aria-current', index === this.page ? 'true' : 'false');
      });
    }

    update() {
      if (this.mode() === 'mobile') {
        this.track.style.transform = '';
        this.updateControls();
        return;
      }

      this.viewport.scrollLeft = 0;

      const columns = this.columnsPerRow();
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const itemWidth = (
        this.viewport.clientWidth - ((columns - 1) * gap)
      ) / columns;
      const distance = this.page * columns * (itemWidth + gap);

      this.track.style.transform = `translate3d(${-distance}px, 0, 0)`;
      this.updateControls();
    }
  }

  customElements.define('logo-list-slider', LogoListSlider);
}
